/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { CollectionData } from '../Common.types';

import { useCallback, useMemo, useRef } from 'preact/hooks';

type UseDataManagerProps<K, D> = {
  data?: D[];
  getRowKey: (data: D) => K;
  hasMore: boolean;
  onLoadMore: () => void;
};

/**
 * Hook that manages data on collection components
 */
export function useDataManager<K extends string | number, D>({
  data,
  getRowKey,
  hasMore,
  onLoadMore
}: UseDataManagerProps<K, D>) {
  const managedData: Array<CollectionData<K, D>> = useMemo(() => {
    return data != null ? data.map((d: D) => ({ data: d, metadata: { key: getRowKey(d) } })) : [];
  }, [data, getRowKey]);

  // debounce loadMore calls to prevent state changes from triggering multiple calls
  const loadMoreDataRef = useRef<D[]>();
  if (!hasMore) {
    // clear loadMoreData cache once there is no more data available
    loadMoreDataRef.current = undefined;
  }
  const onManagedLoadMore = useCallback(() => {
    if (data === loadMoreDataRef.current) {
      return;
    }
    loadMoreDataRef.current = data;
    return onLoadMore();
  }, [data, onLoadMore]);

  return { managedData, onManagedLoadMore };
}
